import { useQuery } from '@tanstack/react-query';
import { getKeywordMetrics, getKeywordMetricsById, getKeywordAIAnalysis, getKeywordMetricsBatch, BatchKeywordMetrics } from '@/lib/apis';
import { KeywordAIAnalysisResponse } from '@/lib/types';

export interface KeywordMetrics {
  keyword: string;
  avg_monthly_searches: number;
  competition_index: number;
  low_top_of_page_bid: number;
  high_top_of_page_bid: number;
  alternative_keywords: string[];
  metrics: {
    reach: {
      value: number;
      score?: number;
      rank?: number;
      total_keywords?: number;
      category_average?: number;
      percentage: number;
      top_percentile?: number;
      description: string;
    };
    competitiveness: {
      value: string;
      score: number;
      description: string;
    };
    landingPageRelevance: {
      score: number;
      description: string;
    };
    brandRelevance: {
      score: number;
      description: string;
    };
    overallAlignment: {
      score: number;
      description: string;
    };
  };
}

// Response from our new database-backed metrics API
export interface StoredKeywordMetrics {
  keyword_id: number;
  keyword_text: string;
  avg_monthly_searches: number | null;
  competition: string | null; // LOW, MEDIUM, HIGH
  competition_index: number | null; // 0-1
  low_top_of_page_bid: number | null;
  high_top_of_page_bid: number | null;
  location_id: number;
  language_id: number;
  last_updated: string;
  ai_analysis?: {
    landing_page_relevance_score: number;
    brand_relevance_score: number;
    overall_alignment_score: number;
    grade: string;
    recommendation: string;
    insights: string[];
    last_analyzed: string;
  };
  metrics?: {
    reach: {
      value: number;
      score: number;
      rank?: number;
      total_keywords?: number;
      category_average?: number;
      percentage: number;
      top_percentile: number;
      description: string;
    };
    competitiveness: {
      value: string;
      score: number;
      description: string;
    };
    landingPageRelevance: {
      score: number;
      description: string;
    };
    brandRelevance: {
      score: number;
      description: string;
    };
    overallAlignment: {
      score: number;
      description: string;
    };
  };
}

export const useKeywordMetrics = (keyword: string) => {
  return useQuery<KeywordMetrics>({
    queryKey: ['keyword-metrics', keyword],
    queryFn: () => getKeywordMetrics(keyword),
    enabled: !!keyword,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// New hook for database-backed metrics using keyword ID.
// Cache key is the canonical ['keyword-metrics-by-id', keywordId, campaignId]
// shape shared with useKeywordMetricsFromDatabase so the same data is never
// cached twice under diverging keys (#5089).
export const useKeywordMetricsById = (keywordId: number, campaignId?: number) => {
  return useQuery<StoredKeywordMetrics>({
    queryKey: ['keyword-metrics-by-id', keywordId, campaignId],
    queryFn: () => getKeywordMetricsById(keywordId, campaignId),
    enabled: !!keywordId,
    staleTime: 10 * 60 * 1000, // 10 minutes (database data is more stable)
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Database-only keyword metrics hook
export const useKeywordMetricsFromDatabase = (keyword: { id?: number; text: string }, campaignId?: number) => {
  const storedMetricsQuery = useQuery<StoredKeywordMetrics>({
    queryKey: ['keyword-metrics-by-id', keyword.id, campaignId],
    queryFn: () => getKeywordMetricsById(keyword.id!, campaignId),
    enabled: !!keyword.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Return the API response directly since backend now provides processed metrics
  return {
    data: storedMetricsQuery.data,
    isLoading: storedMetricsQuery.isLoading,
    isError: storedMetricsQuery.isError,
    error: storedMetricsQuery.error,
  };
};

// AI Analysis hook for keyword insights
export const useKeywordAIAnalysis = (keywordText: string, campaignId?: number, options?: { enabled?: boolean }) => {
  return useQuery<KeywordAIAnalysisResponse>({
    queryKey: ['keyword-ai-analysis', keywordText, campaignId],
    queryFn: () => {
      if (!campaignId) {
        throw new Error('Campaign ID is required for AI analysis');
      }
      return getKeywordAIAnalysis(keywordText, campaignId, { includeAlternatives: true });
    },
    enabled: !!keywordText && !!campaignId && (options?.enabled !== false),
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes - backend already caches for 7 days
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2, // Retry failed requests twice
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

// Batch hook for fetching metrics for multiple keywords in a single API call
export const useBatchKeywordMetrics = (keywordIds: number[]) => {
  const validIds = keywordIds.filter(id => id != null && id > 0);

  return useQuery<BatchKeywordMetrics[]>({
    queryKey: ['keyword-metrics-batch', validIds.sort().join(',')],
    queryFn: () => getKeywordMetricsBatch(validIds),
    enabled: validIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};